import { RiPlayListLine } from "@remixicon/react";
import { Button } from "@workspace/ui/components/button";
import { Card } from "@workspace/ui/components/card";
import type { ChangeEvent } from "react";
import type { BookmarkFilters } from "@/components/shared/filters/bookmark/bookmark-filter";
import type { TabVariant } from "../use-tab-variant";

interface ArtistsEmptyStateProps {
	variant: TabVariant;
	showOnlySavedArtists: boolean;
	authState: boolean;
	actualSearchTerm: string;
	actualSelectedGenre: string;
	bookmarkFilters: BookmarkFilters;
	onAuthRequired?: (message?: string) => void;
	onBookmarkFiltersChange?: (filters: BookmarkFilters) => void;
	handleGenreChange: (genre: string) => void;
	handleSearchChange: (e: ChangeEvent<HTMLInputElement>) => void;
}

export function ArtistsEmptyState({
	variant,
	showOnlySavedArtists,
	authState,
	actualSearchTerm,
	actualSelectedGenre,
	bookmarkFilters,
	onAuthRequired,
	onBookmarkFiltersChange,
	handleGenreChange,
	handleSearchChange,
}: ArtistsEmptyStateProps) {
	return (
		<Card className="p-8 text-center">
			<div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-muted">
				<RiPlayListLine className="h-6 w-6 text-muted-foreground" />
			</div>
			<h3 className="mb-1 font-semibold text-xl">
				{variant === "public" && showOnlySavedArtists && !authState
					? "Sign In Required"
					: "No Artists Found"}
			</h3>
			<p className="mb-4 text-muted-foreground text-sm">
				{actualSearchTerm
					? `No artists match "${actualSearchTerm}".`
					: actualSelectedGenre !== "all"
						? `No artists found for ${actualSelectedGenre === "no-genre" ? "unknown genre" : `${actualSelectedGenre} genre`}.`
						: variant === "personal" &&
								(bookmarkFilters.saved ||
									bookmarkFilters.rated ||
									bookmarkFilters.stream)
							? "No artists match the selected filters."
							: variant === "public" && showOnlySavedArtists
								? authState
									? "No saved artists found."
									: "Sign in to save artists and create your personalized festival schedule."
								: "No artists available for this festival."}
			</p>
			{/* Action buttons for clearing filters */}
			{(actualSearchTerm ||
				actualSelectedGenre !== "all" ||
				(variant === "personal" &&
					(bookmarkFilters.saved ||
						bookmarkFilters.rated ||
						bookmarkFilters.stream)) ||
				(variant === "public" && showOnlySavedArtists)) && (
				<div className="mt-4 flex justify-center gap-2">
					{variant === "public" &&
					showOnlySavedArtists &&
					!authState &&
					onAuthRequired ? (
						<Button
							onClick={() => onAuthRequired("Sign in to save artists")}
							size="sm"
						>
							Sign In
						</Button>
					) : (
						<Button
							variant="secondary"
							size="sm"
							onClick={() => {
								// Clear ALL filters to show all artists
								if (actualSearchTerm) {
									handleSearchChange({
										target: { value: "" },
									} as ChangeEvent<HTMLInputElement>);
								}
								if (actualSelectedGenre !== "all") {
									handleGenreChange("all");
								}
								if (onBookmarkFiltersChange) {
									// Clear all bookmark filters for both variants
									onBookmarkFiltersChange({
										saved: false,
										rated: false,
										following: false,
										stream: false,
									});
								}
							}}
						>
							Show All Artists
						</Button>
					)}
				</div>
			)}
		</Card>
	);
}
